// ═══════════════════════════════════════════════════════════════
// BOUNTIFUL SEA - DICE UTILITIES
// ═══════════════════════════════════════════════════════════════

import { DiceRoll } from '../../types/bountifulSea';

/**
 * Roll a single d6
 */
export function rollD6(): number {
  return Math.floor(Math.random() * 6) + 1;
}

/**
 * Roll multiple d6 dice
 */
export function rollMultipleD6(count: number): number[] {
  const results: number[] = [];
  for (let i = 0; i < count; i++) {
    results.push(rollD6());
  }
  return results;
}

/**
 * Roll dice for a voyage - one Tide Die and multiple Scoundrel Dice
 */
export function rollVoyageDice(scoundrelDiceCount: number): DiceRoll {
  const tideDie = rollD6();
  const scoundrelDice = rollMultipleD6(scoundrelDiceCount);
  const total = tideDie + scoundrelDice.reduce((sum, die) => sum + die, 0);

  return {
    tideDie,
    scoundrelDice,
    total,
  };
}

/**
 * Calculate the minimum possible total for a given number of scoundrel dice
 * (all dice roll 1)
 */
export function getMinPossibleRoll(scoundrelDiceCount: number): number {
  return 1 + scoundrelDiceCount; // Tide die (1) + scoundrel dice (all 1s)
}

/**
 * Calculate the maximum possible total for a given number of scoundrel dice
 * (all dice roll 6)
 */
export function getMaxPossibleRoll(scoundrelDiceCount: number): number {
  return 6 + scoundrelDiceCount * 6; // Tide die (6) + scoundrel dice (all 6s)
}

/**
 * Calculate the expected (average) total for a given number of scoundrel dice
 */
export function getExpectedRoll(scoundrelDiceCount: number): number {
  return 3.5 + scoundrelDiceCount * 3.5;
}

/**
 * Calculate the probability of rolling at least a certain total
 * This is a simplified approximation using normal distribution
 */
export function getProbabilityOfAtLeast(
  target: number,
  scoundrelDiceCount: number
): number {
  const totalDice = 1 + scoundrelDiceCount;
  const mean = totalDice * 3.5;
  const variance = totalDice * 35 / 12; // Variance of a d6 is 35/12
  const stdDev = Math.sqrt(variance);

  // Z-score
  const z = (target - mean) / stdDev;

  // Approximate probability using error function
  // P(X >= target) = 1 - Phi(z) where Phi is CDF of standard normal
  const probability = 1 - normalCDF(z);

  return Math.max(0, Math.min(1, probability));
}

/**
 * Standard normal CDF approximation
 */
function normalCDF(z: number): number {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = z < 0 ? -1 : 1;
  z = Math.abs(z) / Math.sqrt(2);

  const t = 1.0 / (1.0 + p * z);
  const y =
    1.0 -
    ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-z * z);

  return 0.5 * (1.0 + sign * y);
}

/**
 * Calculate suspicion score for a declaration
 * Higher score = more likely to be a lie
 */
export function calculateSuspicionScore(
  declaredTotal: number,
  scoundrelDiceCount: number,
  previousDeclarations: number[]
): number {
  const maxPossible = getMaxPossibleRoll(scoundrelDiceCount);
  const expected = getExpectedRoll(scoundrelDiceCount);

  // Factor 1: How far above expected is the declaration?
  const aboveExpected = declaredTotal - expected;
  const rangeAboveExpected = maxPossible - expected;
  const expectedFactor = Math.max(0, aboveExpected / rangeAboveExpected);

  // Factor 2: How close to max possible?
  const maxFactor = declaredTotal / maxPossible;

  // Factor 3: Did they have to beat a high previous declaration?
  let pressureFactor = 0;
  if (previousDeclarations.length > 0) {
    const lastDeclaration = previousDeclarations[previousDeclarations.length - 1];
    const requiredMinimum = lastDeclaration + 1;
    // If required minimum is close to max, they might be forced to lie
    pressureFactor = requiredMinimum / maxPossible;
  }

  // Combine factors with weights
  const suspicion =
    expectedFactor * 0.4 +
    maxFactor * 0.3 +
    pressureFactor * 0.3;

  return Math.max(0, Math.min(1, suspicion));
}

/**
 * Get a descriptive name for a dice roll total (flavor text)
 */
export function getRollFlavorText(total: number): string | null {
  const flavorTexts: Record<number, string> = {
    2: "Snake Eyes Plus One",
    7: "Lucky Seven",
    11: "The Sailor's Fortune",
    13: "The Kraken's Due",
    21: "Blackjack of the Seas",
    24: "The Captain's Haul",
  };

  return flavorTexts[total] || null;
}

/**
 * Generate dice pip positions for rendering
 */
export function getDicePipPositions(value: number): { x: number; y: number }[] {
  const positions: Record<number, { x: number; y: number }[]> = {
    1: [{ x: 50, y: 50 }],
    2: [
      { x: 25, y: 25 },
      { x: 75, y: 75 },
    ],
    3: [
      { x: 25, y: 25 },
      { x: 50, y: 50 },
      { x: 75, y: 75 },
    ],
    4: [
      { x: 25, y: 25 },
      { x: 75, y: 25 },
      { x: 25, y: 75 },
      { x: 75, y: 75 },
    ],
    5: [
      { x: 25, y: 25 },
      { x: 75, y: 25 },
      { x: 50, y: 50 },
      { x: 25, y: 75 },
      { x: 75, y: 75 },
    ],
    6: [
      { x: 25, y: 25 },
      { x: 75, y: 25 },
      { x: 25, y: 50 },
      { x: 75, y: 50 },
      { x: 25, y: 75 },
      { x: 75, y: 75 },
    ],
  };

  return positions[value] || [];
}
